import { createServerFn } from '@tanstack/react-start';

import { serverFetch } from '@/utils/server-fetch';

export type MembershipOrganization = {
  id: string;
  name: string;
  slug?: string;
  imageUrl?: string;
  teams: MembershipTeam[];
  /**
   * The org answered but its team list did not. Distinct from `teams: []`,
   * which means "this org genuinely has no teams" — callers must offer a retry
   * rather than render an empty workspace or count this org as resolved.
   */
  teamsUnavailable?: boolean;
};

export type MembershipTeam = {
  id: string;
  name: string;
  slug?: string;
  imageUrl?: string;
};

/**
 * A failed lookup is not an empty one. `[]` used to mean both "this
 * researcher belongs to nothing" and "core-api did not answer", and callers
 * acted on the first reading: `resolveActiveDashboard` sent them to
 * onboarding, which sent anyone whose session already listed organizations
 * straight back, looping until the browser gave up. The two answers are now
 * distinguishable, and only `ok` carries organizations.
 */
export type ResearcherMembershipsResult =
  | { ok: true; organizations: MembershipOrganization[] }
  | { ok: false };

type RawOrganization = {
  id?: string;
  _id?: string;
  name?: string;
  slug?: string;
  imageUrl?: string;
};

type RawTeam = {
  id?: string;
  _id?: string;
  name?: string;
  slug?: string;
  imageUrl?: string;
};

/**
 * Load every (organization, team) pair the current researcher belongs to,
 * for use in the `/researchers/select-team` picker and the auto-persist
 * fallback in `/researchers/_authenticated.tsx`. Powered by the v4
 * endpoints already used elsewhere in the app:
 *
 *  - `GET /v4/organizations`                               user's orgs
 *  - `GET /v4/organizations/:organizationId/teams`         user's teams (per org)
 *
 * Teams are fetched in parallel across orgs. A single failed team list marks
 * that org `teamsUnavailable` rather than taking down the whole picker — but
 * it is never flattened into `teams: []`, because the caller cannot tell an
 * outage from a genuinely empty org and would strand the user on a notice
 * with no way forward.
 *
 * The team list is org-wide: `findAllForOrganization` does not filter to the
 * caller's own memberships. Deciding anything from its length would offer
 * teams the user cannot join, so callers intersect it with the session's
 * `user.teams` before counting or rendering.
 *
 * Returns `{ ok: false }` when core-api could not be reached or refused the
 * request, and `{ ok: true, organizations: [] }` when it answered and the
 * researcher genuinely belongs to nothing. Callers must not conflate them.
 *
 * A failure to list *teams* within a reachable org does not fail the whole
 * lookup: the org is returned with `teamsUnavailable`, so one flaky org does
 * not blank out the others and the caller can still offer a retry.
 */
export const fetchResearcherMemberships = createServerFn()
  .validator((data: { userId: string }) => data)
  .handler(
    async ({ data: { userId } }): Promise<ResearcherMembershipsResult> => {
      try {
        const orgsResponse = await serverFetch({
          path: '/v4/organizations',
          method: 'GET',
          userId,
        });
        if (!orgsResponse.ok) return { ok: false };

        const orgsJson: RawOrganization | RawOrganization[] =
          await orgsResponse.json();
        const orgs = Array.isArray(orgsJson) ? orgsJson : [orgsJson];

        const memberships = await Promise.all(
          orgs.map(async (org): Promise<MembershipOrganization | null> => {
            const orgId = org.id ?? org._id;
            if (!orgId) return null;
            const orgName = org.name ?? 'Untitled organization';

            let teams: MembershipTeam[] = [];
            let teamsUnavailable = false;
            try {
              const teamsResponse = await serverFetch({
                path: `/v4/organizations/${orgId}/teams`,
                method: 'GET',
                userId,
              });
              if (teamsResponse.ok) {
                const teamsJson = (await teamsResponse.json()) as
                  | RawTeam[]
                  | RawTeam;
                const rawTeams = Array.isArray(teamsJson)
                  ? teamsJson
                  : [teamsJson];
                const mapped: (MembershipTeam | null)[] = rawTeams.map(
                  (team) => {
                    const id = team.id ?? team._id;
                    if (!id || !team.name) return null;
                    const result: MembershipTeam = { id, name: team.name };
                    if (team.slug) result.slug = team.slug;
                    if (team.imageUrl) result.imageUrl = team.imageUrl;
                    return result;
                  },
                );
                teams = mapped.filter((t): t is MembershipTeam => t !== null);
              } else {
                teamsUnavailable = true;
              }
            } catch (error) {
              console.error(
                `[fetch-researcher-memberships] Failed to fetch teams for org ${orgId}:`,
                error,
              );
              teamsUnavailable = true;
            }

            const membership: MembershipOrganization = {
              id: orgId,
              name: orgName,
              slug: org.slug,
              imageUrl: org.imageUrl,
              teams,
            };
            if (teamsUnavailable) membership.teamsUnavailable = true;
            return membership;
          }),
        );

        return {
          ok: true,
          organizations: memberships.filter(
            (m): m is MembershipOrganization => m !== null,
          ),
        };
      } catch (error) {
        console.error(
          '[fetch-researcher-memberships] Failed to fetch memberships:',
          error,
        );
        return { ok: false };
      }
    },
  );
